import { Component, OnDestroy, OnInit } from "@angular/core";
import {
    TCI_COMPONENT_MODE,
    TCI_ENV_CTRL_ACTION_TYPE,
    ITCIAppEnvControlProviderRequest,
    ITCIApplicationDeployRequest,
    TCITenantSelectorType,
    TCIApplicationDeploymentMode,
    ITCIAppAutoScaleConfig,
    USER_ROLES
} from "@tci/common";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../../../ng-app-details-menu";
import { AbstractTCITenantComponent } from "../../../../../ng-tenant/tci-tenant-component";
import { BaseSubmenuAutoScalingService } from "./base-submenu-auto-scaling.service";

import { Subscription } from "rxjs";
import * as lodash from "lodash";

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_ENV_AUTO_SCALE.toString(),
    templateUrl: "./base-submenu-auto-scaling.component.html",
    styleUrls: ["./base-submenu-auto-scaling.component.less"]
})
export class BaseSubmenuAutoScalingComponent<E, I extends BaseAppDetailMenuComponentInput, O extends BaseAppDetailMenuComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O> implements OnInit, OnDestroy {
    threshold: number[];
    defaultAutoScaleConfig: ITCIAppAutoScaleConfig;
    disableField = false;
    isRORoleUser = false;

    isSubMenu = false; // To toggle mode

    // Subscriptions collection
    private subscriptions: Subscription[] = [];

    constructor(private autoScalingSrv: BaseSubmenuAutoScalingService) {
        super();
    }

    ngOnInit(): void {
        this.isSubMenu = this.input.mode !== TCI_COMPONENT_MODE.COMP_MODE_APP_DETAIL_HEADER;

        // Request current state
        const stateRequest: ITCIApplicationDeployRequest = {
            mode: TCIApplicationDeploymentMode.MODE_APP_DETAILS,
            appId: this.input.application.id,
            application: this.input.application
        };

        this.subscriptions.push(
            this.autoScalingSrv
                .getEnvCtrlService()
                .getTenantDeployProvider(this.input.tenantAppType)
                .isBusy(stateRequest)
                .subscribe(busy => {
                    this.disableField = busy;
                })
        );

        this.autoScalingSrv.getEnvCtrlService().getUserAuthForEnv()
            .subscribe(userAuth => {
                this.isRORoleUser = (userAuth.role === USER_ROLES.RO);
            });
        this.initAutoScaling();

        this.subscriptions.push(this.autoScalingSrv.getEnvCtrlService().getStatusSubject()
            .subscribe((res: TCI_ENV_CTRL_ACTION_TYPE) => {
                if (res === TCI_ENV_CTRL_ACTION_TYPE.REFRESH) {
                    this.initAutoScaling();
                }
            }));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    initAutoScaling() {
        this.defaultAutoScaleConfig = this.formatAutoScaling(this.input.application.autoScaleConfig);
        this.autoScalingSrv.getEnvCtrlService().initAppAutoScale(this.generateReqInput())
            .subscribe(res => {
                this.defaultAutoScaleConfig = this.formatAutoScaling(res.autoScaleConfig);
            });
    }

    formatAutoScaling(_autoScaleConfig: ITCIAppAutoScaleConfig) {
        let autoScaleConfig: ITCIAppAutoScaleConfig = lodash.clone(_autoScaleConfig);
        if (autoScaleConfig) {
            autoScaleConfig.autoScaleEnabled = String(autoScaleConfig.autoScaleEnabled).toLowerCase() === "true";

            if (!isNaN(autoScaleConfig.highLoadFactor)) {
                autoScaleConfig.highLoadFactor = parseInt(autoScaleConfig.highLoadFactor.toString(), 10);
            }

            if (!isNaN(autoScaleConfig.lowLoadFactor)) {
                autoScaleConfig.lowLoadFactor = parseInt(autoScaleConfig.lowLoadFactor.toString(), 10);
            }
        } else {
            // Noto: autoscale default data for TCI1 app
            autoScaleConfig = {
                autoScaleEnabled: false,
                lowLoadFactor: 30,
                highLoadFactor: 70
            };
        }
        this.threshold = [autoScaleConfig.lowLoadFactor, autoScaleConfig.highLoadFactor];
        return autoScaleConfig;
    }

    isPillVisible() {
        return this.input.application.autoScaleConfig && String(this.input.application.autoScaleConfig.autoScaleEnabled).toLowerCase() === "true";
    }

    generateReqInput(): ITCIAppEnvControlProviderRequest {
        return {
            actionType: TCI_ENV_CTRL_ACTION_TYPE.APP_AUTO_SCALE,
            appId: this.input.application.id,
            application: this.input.application,
            tenantType: this.input.application.type
        };
    }

    updateChange() {
        this.autoScalingSrv.getEnvCtrlService().setTabData(TCI_ENV_CTRL_ACTION_TYPE.APP_AUTO_SCALE, {
            "autoScaleEnabled": String(this.defaultAutoScaleConfig.autoScaleEnabled),
            "lowLoadFactor": String(lodash.min(this.threshold)),
            "highLoadFactor": String(lodash.max(this.threshold))
        });
    }
}
